// Klettern, Leiter-Griff und Mantling (Konzept §11 / §17).
//
// Warum eigene Datei: player.js ist schon das Physik-Herz; Kletterzustände
// (Halten → Leiter → Deck, Kanten hochdrücken) brauchen klaren Raum ohne
// den Boden-/Ride-Pfad weiter zu vergrößern.
//
// Kernregeln (nicht arcadig):
//  - Greifen nur an sichtbaren Leitern/Zonen, nicht an glatten Wänden.
//  - Reichweite von der HAND, getrennt in horizontal / vertikal.
//  - Halten von E: Zeitfenster aus Physik (mitlaufen), nicht nur Speed-Check.
//  - Am fahrenden Zug: Mantling nur unter GRAB_MAX_RELATIVE_SPEED.

import * as THREE from 'three';
import {
  CLIMB_SPEED,
  GRAB_MAX_RELATIVE_SPEED,
  GRAB_HORIZ,
  GRAB_VERT_UP,
  GRAB_VERT_DOWN,
  GRAB_HOLD_TIME,
  GRAB_HOLD_DECAY,
  HAND_HEIGHT_STAND,
  HAND_HEIGHT_CROUCH,
  MANTLE_MIN_HEIGHT,
  MANTLE_MAX_HEIGHT,
  MANTLE_DURATION,
  MANTLE_FORWARD,
  PLAYER_HEIGHT,
  PLAYER_RADIUS,
  MAX_STEP_HEIGHT,
} from '../../shared/constants.js';
import { trackTangent } from '../../shared/track.js';

// Modul-Puffer — nichts pro Substep allokieren.
const _ladderWorld = new THREE.Vector3();
const _handWorld = new THREE.Vector3();
const _tmp = new THREE.Vector3();
const _carVel = new THREE.Vector3();
const _rel = new THREE.Vector3();
const _tangent = { x: 0, y: 0, z: 0 };
const _local = new THREE.Vector3();
const _world = new THREE.Vector3();
const _mantleHit = {
  ok: false,
  topY: 0,
  standX: 0,
  standZ: 0,
  carId: null,
  localX: 0,
  localY: 0,
  localZ: 0,
  kind: 'static', // 'static' | 'car'
};
const _mantleQuery = {
  minRise: MANTLE_MIN_HEIGHT,
  maxRise: MANTLE_MAX_HEIGHT,
  forward: MANTLE_FORWARD,
  radius: PLAYER_RADIUS,
  bodyHeight: PLAYER_HEIGHT,
};

function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

/** Handhöhe über den Füßen — geduckt niedriger, in der Luft gleiche Reichweite. */
export function handHeightAboveFeet(st) {
  return st.crouching ? HAND_HEIGHT_CROUCH : HAND_HEIGHT_STAND;
}

/** Prompt-Wort je nach Haltefortschritt (kein Prozentbalken, Konzept §8). */
export function holdPromptWord(progress, tooFast) {
  if (tooFast) return 'zu schnell';
  if (progress <= 0.02) return 'greifen';
  if (progress < 0.45) return 'greifen …';
  if (progress < 0.85) return 'halt fest';
  return 'fast';
}

/**
 * Nächsten Punkt auf einer vertikalen Leiter-Zone relativ zur Hand berechnen.
 * Liefert null wenn außerhalb horizontaler oder vertikaler Reichweite.
 *
 * @returns {{ horiz: number, grabY: number, score: number } | null}
 */
const _reach = { horiz: 0, grabY: 0, score: 0 };

export function ladderReach(handY, feetX, feetZ, ladderWorldX, ladderWorldZ, y0, y1) {
  const dx = feetX - ladderWorldX;
  const dz = feetZ - ladderWorldZ;
  const horiz = Math.hypot(dx, dz);
  if (horiz > GRAB_HORIZ) return null;

  // Vertikal erreichbares Fenster um die Hand.
  const winLo = handY - GRAB_VERT_DOWN;
  const winHi = handY + GRAB_VERT_UP;
  // Überlappung Leitersegment ∩ Fenster.
  const segLo = Math.max(y0, winLo);
  const segHi = Math.min(y1, winHi);
  if (segHi < segLo) return null;

  // Wiederverwendeter Puffer: Diese Funktion läuft für jede Leiter in jedem
  // Physik-Substep. Der Aufrufer liest die Werte sofort und hält keine Referenz.
  _reach.horiz = horiz;
  _reach.grabY = clamp(handY, segLo, segHi);
  _reach.score = horiz + Math.abs(_reach.grabY - handY) * 0.35;
  return _reach;
}

/**
 * Weltposition eines Leiter-Ankers (x,z,y lokal) über train.localToWorld.
 */
function ladderPointWorld(train, car, lx, ly, lz, out) {
  _local.set(lx, ly, lz);
  if (train.localToWorld) {
    train.localToWorld(car.id, _local, out);
  } else if (car.group) {
    out.copy(_local);
    car.group.localToWorld(out);
  } else {
    out.set(lx, ly, lz);
  }
  return out;
}

/**
 * Sucht die beste Leiter in Reichweite. Schreibt Ergebnis in player._nearGrab.
 * canGrab = true wenn greifbar (auch wenn zu schnell — Prompt sagt es).
 */
export function tryGrabProximity(player, ctx) {
  const st = player.state;
  const train = ctx.train;
  if (!train?.carAt) {
    player._nearGrab = null;
    return;
  }

  // Suchradius: horizontal + etwas Puffer für carAt.
  const car = train.carAt(st.pos, GRAB_HORIZ + 1.2);
  if (!car) {
    player._nearGrab = null;
    return;
  }

  const ladders = car.ladders?.length ? car.ladders : null;
  const grabs = ladders ? null : (car.grabs ?? null);
  const sourceCount = ladders?.length ?? grabs?.length ?? 0;
  if (sourceCount === 0) {
    player._nearGrab = null;
    return;
  }

  const handY = st.pos.y + handHeightAboveFeet(st);
  _handWorld.set(st.pos.x, handY, st.pos.z);

  let bestLadder = null;
  let bestGrab = null;
  let bestScore = Infinity;
  let bestGrabY = 0;
  let bestWorldX = 0;
  let bestWorldZ = 0;
  let bestWorldY0 = 0;
  let bestWorldY1 = 0;

  for (let i = 0; i < sourceCount; i++) {
    // Fallback-Punktgriffe bleiben degenerierte 0-Längen-Zonen, ohne dafür
    // pro Substep ein neues Array und neue Objekte anzulegen.
    const lad = ladders ? ladders[i] : null;
    const point = ladders ? null : grabs[i];
    const lx = lad ? lad.x : point.x;
    const lz = lad ? lad.z : point.z;
    const ly0 = lad ? lad.y0 : point.y;
    const ly1 = lad ? lad.y1 : point.y;

    // Welt-XZ der Leitersäule (y egal für XZ — nehmen y0).
    ladderPointWorld(train, car, lx, ly0, lz, _ladderWorld);
    const wx = _ladderWorld.x;
    const wz = _ladderWorld.z;
    // y0/y1 in Welt: localToWorld für beide Enden (Überhöhung klein, y ≈ offset).
    ladderPointWorld(train, car, lx, ly0, lz, _tmp);
    const worldY0 = _tmp.y;
    ladderPointWorld(train, car, lx, ly1, lz, _tmp);
    const worldY1 = _tmp.y;
    const yLo = Math.min(worldY0, worldY1);
    const yHi = Math.max(worldY0, worldY1);

    const reach = ladderReach(handY, st.pos.x, st.pos.z, wx, wz, yLo, yHi);
    if (!reach || reach.score >= bestScore) continue;
    bestScore = reach.score;
    bestLadder = lad;
    bestGrab = point;
    bestGrabY = reach.grabY;
    bestWorldX = wx;
    bestWorldZ = wz;
    bestWorldY0 = yLo;
    bestWorldY1 = yHi;
  }

  if (!bestLadder && !bestGrab) {
    player._nearGrab = null;
    return;
  }

  const info = player._nearGrabCache;
  info.carId = car.id;
  info.ladder = bestLadder;
  info.grabY = bestGrabY;
  info.worldX = bestWorldX;
  info.worldZ = bestWorldZ;
  info.worldY0 = bestWorldY0;
  info.worldY1 = bestWorldY1;
  info.rel = relativeSpeed(player, ctx, car);
  const source = bestLadder ?? bestGrab;
  info.grab.x = source.x;
  info.grab.y = bestLadder
    ? bestLadder.y0 + (bestGrabY - bestWorldY0)
    : bestGrab.y;
  info.grab.z = source.z;
  info.grab.side = source.side ?? null;

  st.canGrab = true;
  st.grabCarId = car.id;
  player._nearGrab = info;
  st.prompt = holdPromptWord(
    player.grabHoldT / GRAB_HOLD_TIME,
    info.rel > GRAB_MAX_RELATIVE_SPEED,
  );
}

/** Horizontal-Relativgeschwindigkeit Spieler↔Waggon (m/s, Echtzeit). */
export function relativeSpeed(player, ctx, car) {
  const train = ctx.train;
  const real = train?.realSpeed ?? 0;
  const s = car?.s ?? train?.headS ?? 0;
  trackTangent(s, _tangent);
  const dir = train?.direction ?? 1;
  _carVel.set(_tangent.x * real * dir, _tangent.y * real * dir, _tangent.z * real * dir);
  _rel.set(
    player.state.velocity.x - _carVel.x,
    0,
    player.state.velocity.z - _carVel.z,
  );
  return Math.hypot(_rel.x, _rel.z);
}

/**
 * Halte-Fortschritt für E. Aufruf pro Substep im ground/air.
 * Gibt true zurück wenn der Griff jetzt zünden soll.
 */
export function updateGrabHold(player, dt, ctx) {
  const st = player.state;
  const input = ctx.input;
  const held = !!(input && typeof input.down === 'function' && input.down('interact'));
  const info = player._nearGrab;
  const inRange = !!(st.canGrab && info);
  const tooFast = inRange && info.rel > GRAB_MAX_RELATIVE_SPEED;

  if (held && inRange && !tooFast && player.stunT <= 0) {
    player.grabHoldT = Math.min(GRAB_HOLD_TIME, player.grabHoldT + dt);
    st.prompt = holdPromptWord(player.grabHoldT / GRAB_HOLD_TIME, false);
    if (player.grabHoldT >= GRAB_HOLD_TIME - 1e-6) {
      player.grabHoldT = 0;
      return true;
    }
    return false;
  }

  // Zu schnell: Fortschritt stirbt, Prompt bleibt ehrlich.
  if (inRange && tooFast) {
    player.grabHoldT = Math.max(0, player.grabHoldT - dt * GRAB_HOLD_DECAY);
    st.prompt = 'zu schnell';
    return false;
  }

  // Loslassen / außer Reichweite: zügiger Verfall.
  if (player.grabHoldT > 0) {
    player.grabHoldT = Math.max(0, player.grabHoldT - dt * GRAB_HOLD_DECAY);
  }
  return false;
}

/**
 * Griff abgeschlossen → Kletterzustand an der Leiter.
 * Direkter Einstieg in climb (kein extra Hang-Tap), Konzept: „dann auf der Leiter".
 */
export function attemptLadderGrab(player, ctx) {
  const st = player.state;
  const info = player._nearGrab;
  if (!info) return false;
  const train = ctx.train;
  const car = train?.carById?.(info.carId) ?? train?.cars?.find((c) => c.id === info.carId);
  if (!car) return false;

  const rel = relativeSpeed(player, ctx, car);
  if (rel > GRAB_MAX_RELATIVE_SPEED) {
    stumble(player, ctx, car);
    return false;
  }

  const lad = info.ladder;
  // Lokale Greifhöhe: Welt-grabY → lokal. Füße unter dem Griff, aber nicht
  // unter y0 — sonst löst stepClimb im selben Moment wieder ab.
  const handLocalY = lad.y0 + (info.grabY - info.worldY0);
  const feetLocalY = clamp(handLocalY - 1.05, lad.y0 - 0.05, lad.y1 - 0.35);

  player.carLocal.set(lad.x * 0.92, feetLocalY, lad.z);
  // Leicht nach außen, damit man nicht in der Hülle steckt — Lock auf Säule.
  st.onCarId = car.id;
  st.mode = 'climb';
  st.grounded = false;
  st.velocity.set(0, 0, 0);
  player.hangGrab = null;
  player.climbLadder = {
    carId: car.id,
    x: lad.x,
    z: lad.z,
    y0: lad.y0,
    y1: lad.y1,
    side: lad.side ?? null,
  };
  player.grabHoldT = 0;
  syncWorldFromLocal(player, ctx);
  st.prompt = 'klettern';
  return true;
}

/**
 * Direkter Griff (Tests / Kompat): alter Hang-Pfad wenn grab-Punkt gesetzt
 * ohne ladder-Zone. Produktionspfad nutzt attemptLadderGrab.
 */
export function attemptGrabCompat(player, ctx) {
  const st = player.state;
  const info = player._nearGrab;
  if (!info) return;
  // Wenn Leiter-Daten da: neuer Pfad.
  if (info.ladder && info.worldY0 != null) {
    attemptLadderGrab(player, ctx);
    return;
  }
  const train = ctx.train;
  const car = train?.carById?.(info.carId) ?? train?.cars?.find((c) => c.id === info.carId);
  if (!car) return;
  const rel = relativeSpeed(player, ctx, car);
  if (rel > GRAB_MAX_RELATIVE_SPEED) {
    stumble(player, ctx, car);
    return;
  }
  const g = info.grab;
  player.carLocal.set(g.x, g.y - 1.1, g.z);
  st.onCarId = car.id;
  st.mode = 'hang';
  st.grounded = false;
  st.velocity.set(0, 0, 0);
  player.hangGrab = { carId: car.id, x: g.x, y: g.y, z: g.z };
  player.climbLadder = null;
  syncWorldFromLocal(player, ctx);
  st.prompt = 'hochziehen';
}

export function stumble(player, ctx, car) {
  const st = player.state;
  const rel = relativeSpeed(player, ctx, car);
  trackTangent(car?.s ?? 0, _tangent);
  const rx = _tangent.z;
  const rz = -_tangent.x;
  const side = Math.sign(st.lateral || 1) || 1;
  st.velocity.x = rx * side * (3.5 + rel * 0.15) - _tangent.x * 1.2;
  st.velocity.z = rz * side * (3.5 + rel * 0.15) - _tangent.z * 1.2;
  st.velocity.y = 2.2;
  st.grounded = false;
  st.mode = 'air';
  st.onCarId = null;
  player.stunT = 0.85;
  player.runCharge = 0;
  player.grabHoldT = 0;
  player.climbLadder = null;
  player.hangGrab = null;
  if (ctx.log) ctx.log('du verlierst den Halt');
}

export function stepHang(player, dt, ctx) {
  const st = player.state;
  const train = ctx.train;
  const input = ctx.input;

  if (!train || !st.onCarId || !player.hangGrab) {
    detachToAir(player, ctx);
    return;
  }

  player.carLocal.set(player.hangGrab.x, player.hangGrab.y - 1.1, player.hangGrab.z);
  syncWorldFromLocal(player, ctx);
  st.velocity.set(0, 0, 0);
  st.prompt = 'hochziehen';

  const pull = player._interactNow || player._jumpNow;
  if (pull) pullUp(player, ctx);

  if (input) {
    const release = typeof input.keyPressed === 'function'
      ? (input.keyPressed('ControlLeft') || input.keyPressed('ControlRight'))
      : input.justPressed?.('crouch');
    if (release) detachToAir(player, ctx);
  }
}

function pullUp(player, ctx) {
  const st = player.state;
  const train = ctx.train;
  const car = train?.carById?.(st.onCarId);
  if (!car) {
    detachToAir(player, ctx);
    return;
  }

  const decks = car.decks ?? [];
  let deck = null;
  for (const d of decks) {
    if (player.carLocal.x >= d.minX - 0.2 && player.carLocal.x <= d.maxX + 0.2 &&
        player.carLocal.z >= d.minZ - 0.2 && player.carLocal.z <= d.maxZ + 0.2) {
      deck = d;
      break;
    }
  }
  if (!deck && decks.length) {
    deck = decks.reduce((a, b) => (
      Math.abs(b.y - player.carLocal.y) < Math.abs(a.y - player.carLocal.y) ? b : a
    ));
  }

  if (deck) {
    mountDeck(player, ctx, deck);
  } else {
    st.mode = 'climb';
    player.hangGrab = null;
    if (!player.climbLadder && player.hangGrab) {
      // unreachable — hangGrab already null
    }
  }
}

function mountDeck(player, ctx, deck) {
  const st = player.state;
  player.carLocal.y = deck.y;
  player.carLocal.x = clamp(player.carLocal.x, deck.minX + 0.15, deck.maxX - 0.15);
  player.carLocal.z = clamp(player.carLocal.z, deck.minZ + 0.15, deck.maxZ - 0.15);
  st.mode = deck.kind === 'inside' ? 'inside' : 'ride';
  st.grounded = true;
  player.carVy = 0;
  player.hangGrab = null;
  player.climbLadder = null;
  player.mantleT = 0;
  syncWorldFromLocal(player, ctx);
}

/**
 * Auf der Leiter: W/S hoch/runter, oben Deck, unten Boden, seitlich abspringen.
 */
export function stepClimb(player, dt, ctx) {
  const st = player.state;
  const train = ctx.train;
  const input = ctx.input;
  if (!train || !st.onCarId) {
    detachToAir(player, ctx);
    return;
  }

  const car = train.carById?.(st.onCarId);
  const lad = player.climbLadder;
  // An der Säule locken (X/Z), Y steuert der Spieler.
  if (lad) {
    player.carLocal.x = lad.x * 0.92;
    player.carLocal.z = lad.z;
  }

  const axis = input ? input.moveAxis() : { x: 0, y: 0 };
  player.carLocal.y += axis.y * CLIMB_SPEED * dt;

  // Seitlich abspringen (Gefahr, Konzept §11) — Jump oder Strafe+Interact.
  const sideJump = player._jumpNow || (player._interactNow && Math.abs(axis.x) > 0.4);
  if (sideJump) {
    detachToAir(player, ctx, true);
    return;
  }

  // Oberkante: Deck übernehmen.
  const topY = lad ? lad.y1 : (car?.type?.height ?? 3);
  const decks = car?.decks ?? [];
  if (player.carLocal.y >= topY - 0.2) {
    // Nächstes Deck in XZ-Nähe.
    let deck = null;
    let best = Infinity;
    for (const d of decks) {
      const cx = clamp(player.carLocal.x, d.minX, d.maxX);
      const cz = clamp(player.carLocal.z, d.minZ, d.maxZ);
      const dist = Math.hypot(player.carLocal.x - cx, player.carLocal.z - cz);
      if (dist < best) {
        best = dist;
        deck = d;
      }
    }
    if (deck && best < 1.8) {
      // Zur Deckkante ziehen und mounten.
      player.carLocal.x = clamp(player.carLocal.x * 0.5, deck.minX + 0.2, deck.maxX - 0.2);
      player.carLocal.z = clamp(player.carLocal.z, deck.minZ + 0.15, deck.maxZ - 0.15);
      mountDeck(player, ctx, deck);
      return;
    }
    // Kein Deck: am Top klemmen.
    player.carLocal.y = topY - 0.15;
  }

  // Unterkante: absichtlich runter (S) → absetzen; sonst am untersten Spross halten.
  const botY = lad ? lad.y0 : 0;
  if (player.carLocal.y < botY - 0.05) {
    if (axis.y < -0.15) {
      syncWorldFromLocal(player, ctx);
      detachToAir(player, ctx);
      return;
    }
    player.carLocal.y = botY - 0.02;
  }
  if (player.carLocal.y < -0.6) {
    detachToAir(player, ctx);
    return;
  }

  syncWorldFromLocal(player, ctx);
  st.velocity.set(0, 0, 0);
  st.prompt = 'klettern';
}

/**
 * Mantling: Kante vor dem Spieler in greifbarer Höhe → kurzes Hochziehen.
 * Bewegte Waggons nur unter GRAB_MAX_RELATIVE_SPEED (sonst wäre 50 km/h-Mantle falsch).
 */
export function tryMantle(player, dt, ctx) {
  const st = player.state;
  if (st.mode !== 'ground' && st.mode !== 'air') return false;
  if (player.stunT > 0 || player.mantleT > 0) return false;

  const input = ctx.input;
  const axis = input ? input.moveAxis() : { x: 0, y: 0 };
  const wantForward = axis.y > 0.35;
  const held = !!(input && input.down('interact'));
  // Mantle startet bei E-Halten oder wenn man gegen die Kante läuft (forward).
  if (!held && !wantForward) {
    player.mantleHoldT = Math.max(0, player.mantleHoldT - dt * GRAB_HOLD_DECAY);
    return false;
  }

  const hit = findMantleTarget(player, ctx);
  if (!hit.ok) {
    player.mantleHoldT = Math.max(0, player.mantleHoldT - dt * GRAB_HOLD_DECAY);
    return false;
  }

  // Bewegter Waggon: Relativgeschwindigkeit zählt.
  if (hit.kind === 'car' && hit.carId) {
    // Am Zug nur mit E — reines Vorwärtslaufen gegen die Hülle darf kein
    // Mantle starten (sonst mode=mantle → keine Kollision → Durchlaufen).
    if (!held) {
      player.mantleHoldT = Math.max(0, player.mantleHoldT - dt * GRAB_HOLD_DECAY);
      return false;
    }
    const train = ctx.train;
    const car = train?.carById?.(hit.carId);
    if (car) {
      const rel = relativeSpeed(player, ctx, car);
      if (rel > GRAB_MAX_RELATIVE_SPEED) {
        st.prompt = 'zu schnell';
        player.mantleHoldT = 0;
        return false;
      }
    }
  }

  // Niedrige Kanten laufen über Step — hier nur hohe.
  const rise = hit.topY - st.pos.y;
  if (rise <= MAX_STEP_HEIGHT + 0.08) {
    player.mantleHoldT = 0;
    return false;
  }
  if (rise < MANTLE_MIN_HEIGHT || rise > MANTLE_MAX_HEIGHT) {
    player.mantleHoldT = 0;
    return false;
  }

  // Kurzes Halten auch beim Mantle (etwas kürzer als Leiter-Griff).
  const need = MANTLE_DURATION * 0.55;
  if (held || (wantForward && hit.kind === 'static')) {
    // Statik: Gegen-die-Kante-Laufen füllt langsamer; Zug: nur E (oben).
    const rate = held ? 1.0 : 0.55;
    player.mantleHoldT = Math.min(need, player.mantleHoldT + dt * rate);
    st.prompt = player.mantleHoldT < need * 0.5 ? 'hochdrücken' : 'hoch …';
  }

  if (player.mantleHoldT < need - 1e-6) return false;

  // Start Mantle-Zustand: unbeweglich, dann teleport auf Kante.
  player.mantleHoldT = 0;
  player.mantleT = MANTLE_DURATION;
  player._mantleTarget = {
    topY: hit.topY,
    standX: hit.standX,
    standZ: hit.standZ,
    carId: hit.carId,
    localX: hit.localX,
    localY: hit.localY,
    localZ: hit.localZ,
    kind: hit.kind,
  };
  st.mode = 'mantle';
  st.velocity.set(0, 0, 0);
  st.grounded = false;
  st.prompt = 'hochziehen';
  return true;
}

/** Pro Substep während mode === 'mantle'. */
export function stepMantle(player, dt, ctx) {
  const st = player.state;
  const tgt = player._mantleTarget;
  player.mantleT = Math.max(0, player.mantleT - dt);
  st.velocity.set(0, 0, 0);
  st.prompt = 'hochziehen';

  if (!tgt) {
    st.mode = 'air';
    player.mantleT = 0;
    return;
  }

  // Leichte Annäherung der Füße an die Standposition während der Dauer.
  const t = 1 - (player.mantleT / MANTLE_DURATION);
  const ease = t * t * (3 - 2 * t);

  if (tgt.kind === 'car' && tgt.carId && ctx.train) {
    st.onCarId = tgt.carId;
    player.carLocal.set(
      tgt.localX,
      tgt.localY * ease + (tgt.localY - 0.9) * (1 - ease),
      tgt.localZ,
    );
    // Am Ende exakt auf Deck.
    if (player.mantleT <= 0) {
      player.carLocal.set(tgt.localX, tgt.localY, tgt.localZ);
      const car = ctx.train.carById?.(tgt.carId);
      const decks = car?.decks ?? [];
      const support = decks.find((d) =>
        tgt.localX >= d.minX && tgt.localX <= d.maxX &&
        tgt.localZ >= d.minZ && tgt.localZ <= d.maxZ
      ) ?? decks[0];
      if (support) {
        mountDeck(player, ctx, support);
      } else {
        st.mode = 'ride';
        st.grounded = true;
        syncWorldFromLocal(player, ctx);
      }
      player._mantleTarget = null;
      return;
    }
    syncWorldFromLocal(player, ctx);
    return;
  }

  // Statische Kante (Bahnsteig, Mauer, …).
  st.pos.x = st.pos.x + (tgt.standX - st.pos.x) * Math.min(1, ease * 0.35 + dt * 2);
  st.pos.z = st.pos.z + (tgt.standZ - st.pos.z) * Math.min(1, ease * 0.35 + dt * 2);
  st.pos.y = st.pos.y + (tgt.topY - st.pos.y) * Math.min(1, 0.2 + ease * 0.8);

  if (player.mantleT <= 0) {
    st.pos.x = tgt.standX;
    st.pos.z = tgt.standZ;
    st.pos.y = tgt.topY;
    st.grounded = true;
    st.mode = 'ground';
    st.velocity.set(0, 0, 0);
    player._mantleTarget = null;
    st.prompt = null;
  }
}

/**
 * Sucht eine Mantle-Kante vor dem Spieler.
 * Statik über collision.findMantleEdge; Waggon-Decks über local space.
 */
function findMantleTarget(player, ctx) {
  _mantleHit.ok = false;
  _mantleHit.carId = null;
  _mantleHit.kind = 'static';

  const st = player.state;
  const sy = Math.sin(st.yaw);
  const cy = Math.cos(st.yaw);
  // Vorwärts wie Kamera: (sin, 0, -cos)
  const fx = sy;
  const fz = -cy;
  const px = st.pos.x + fx * MANTLE_FORWARD;
  const pz = st.pos.z + fz * MANTLE_FORWARD;

  // 1) Statische Oberflächen (Bahnsteig, Mauer, Bunker, …)
  const col = ctx.collision ?? ctx.systems?.collision;
  if (col?.findMantleEdge) {
    _mantleQuery.bodyHeight = st.crouching ? 1.1 : PLAYER_HEIGHT;
    const edge = col.findMantleEdge(
      st.pos.x, st.pos.y, st.pos.z, fx, fz, _mantleQuery,
    );
    if (edge) {
      _mantleHit.ok = true;
      _mantleHit.topY = edge.topY;
      _mantleHit.standX = edge.standX;
      _mantleHit.standZ = edge.standZ;
      _mantleHit.kind = 'static';
      return _mantleHit;
    }
  }

  // 2) Waggon-Deck-Kante (nur hoppbarer Zug, boardable).
  // Wichtig: nur von AUSSERHALB der Hülle, und nur wenn die Kante im
  // Mantle-Band liegt (Flachwagen ~1,3 m). Sonst würde Vorwärtslaufen gegen
  // einen stehenden Waggon Mantle triggern → mode mantle überspringt
  // Kollision → Durchlaufen (Regression).
  const train = ctx.train;
  if (!train?.carAt || train.boardable === false) return _mantleHit;

  const car = train.carAt(st.pos, GRAB_HORIZ + 1.5);
  if (!car?.decks?.length || !train.worldToLocal) return _mantleHit;

  // Probe vor den Füßen → lokal.
  _world.set(px, st.pos.y + 1.0, pz);
  train.worldToLocal(car.id, _world, _local);
  // Auch Füße lokal für rise.
  train.worldToLocal(car.id, st.pos, _tmp);

  const halfW = (car.type?.width ?? 3) * 0.5;
  const halfL = (car.type?.length ?? 14) * 0.5;
  // Füße müssen außerhalb der Hülle stehen (Kantengriff, kein Wanddurchdringen).
  const feetOutside =
    Math.abs(_tmp.x) >= halfW - 0.05 || Math.abs(_tmp.z) >= halfL - 0.05;
  if (!feetOutside) return _mantleHit;

  let bestDeck = null;
  let bestDy = Infinity;
  for (const d of car.decks) {
    // Kante: Probe nahe Deckrand, nicht tief im Inneren.
    const cx = clamp(_local.x, d.minX, d.maxX);
    const cz = clamp(_local.z, d.minZ, d.maxZ);
    const edgeDist = Math.hypot(_local.x - cx, _local.z - cz);
    // Außerhalb oder am Rand (≤0.55 m), nicht mitten auf dem Deck stehend.
    const nearEdge = edgeDist <= 0.55 ||
      Math.abs(_local.x) >= halfW - 0.4 ||
      Math.abs(_local.z) >= halfL - 0.4;
    if (!nearEdge) continue;

    const rise = d.y - _tmp.y;
    if (rise < MANTLE_MIN_HEIGHT || rise > MANTLE_MAX_HEIGHT) continue;
    if (rise < bestDy) {
      bestDy = rise;
      bestDeck = d;
    }
  }

  if (!bestDeck) return _mantleHit;

  // Standpunkt: etwas nach innen auf dem Deck.
  const standLocalX = clamp(_local.x, bestDeck.minX + 0.2, bestDeck.maxX - 0.2);
  const standLocalZ = clamp(_local.z, bestDeck.minZ + 0.2, bestDeck.maxZ - 0.2);
  _local.set(standLocalX, bestDeck.y, standLocalZ);
  train.localToWorld(car.id, _local, _world);

  _mantleHit.ok = true;
  _mantleHit.kind = 'car';
  _mantleHit.carId = car.id;
  _mantleHit.topY = _world.y;
  _mantleHit.standX = _world.x;
  _mantleHit.standZ = _world.z;
  _mantleHit.localX = standLocalX;
  _mantleHit.localY = bestDeck.y;
  _mantleHit.localZ = standLocalZ;
  return _mantleHit;
}

export function detachToAir(player, ctx, intentional = false) {
  const st = player.state;
  const train = ctx.train;
  if (train && st.onCarId) {
    const car = train.carById?.(st.onCarId);
    const real = train.realSpeed ?? 0;
    trackTangent(car?.s ?? train.headS ?? 0, _tangent);
    const dir = train.direction ?? 1;
    st.velocity.x = _tangent.x * real * dir;
    st.velocity.z = _tangent.z * real * dir;
    st.velocity.y = intentional ? 1.5 : (player.carVy || 0);
    if (intentional) {
      const side = Math.sign(st.lateral || 1) || 1;
      st.velocity.x += _tangent.z * side * 2.5;
      st.velocity.z += -_tangent.x * side * 2.5;
    }
  }
  st.onCarId = null;
  st.mode = 'air';
  st.grounded = false;
  player.hangGrab = null;
  player.climbLadder = null;
  player.carVy = 0;
  player._mantleTarget = null;
  player.mantleT = 0;
}

export function syncWorldFromLocal(player, ctx) {
  const st = player.state;
  const train = ctx.train;
  if (!train || !st.onCarId) return;
  _local.copy(player.carLocal);
  if (train.localToWorld) {
    train.localToWorld(st.onCarId, _local, _world);
    st.pos.copy(_world);
  } else {
    const car = train.carById?.(st.onCarId);
    if (car?.group) {
      _world.copy(_local);
      car.group.localToWorld(_world);
      st.pos.copy(_world);
    }
  }
}

export {
  GRAB_HOLD_TIME,
  GRAB_MAX_RELATIVE_SPEED,
  MANTLE_DURATION,
  MANTLE_MIN_HEIGHT,
  MANTLE_MAX_HEIGHT,
};
